import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import angularLocaleEn from '@angular/common/locales/en';
import angularLocaleNl from '@angular/common/locales/nl';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { ApplicationConfig } from '@config/application.config';
import { getDatePickerConfig, getDateRangePickerConfig } from '@config/datepicker.config';
import MessageFormatConfig from '@config/message-format.config';
import NgxSpinnerConfig from '@config/ngx-spinner.config';
import NgxsStorageConfig from '@config/ngxs-storage.config';
import ToastrConfig from '@config/toastr.config';
import NgToggleConfig from '@config/ng-toggle-button.config';
import { LayoutModule } from '@core/layout/layout.module';
import { NavigationModule } from '@core/navigation/navigation.module';
import { LanguageVersion } from '@data/common/LanguageVersion';
import { LocaleId } from '@data/common/LocalId';
import { NgSelectConfig, NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import { AUTH_PROVIDER } from '@shared/common/injector-token';
import { AccessTokenInterceptor } from '@shared/interceptor/access-token.interceptor';
import { AppLanguageInterceptor } from '@shared/interceptor/app-language.interceptor';
import { AppTimezoneInterceptor } from '@shared/interceptor/app-timezone.interceptor';
import { AppVersionInterceptor } from '@shared/interceptor/app-version.interceptor';
import { ErrorInterceptor } from '@shared/interceptor/error.interceptor';
import { LoaderInterceptor } from '@shared/interceptor/loader.interceptor';
import { RefreshTokenInterceptor } from '@shared/interceptor/refresh-token.interceptor';
import { CognitoAuthProvider } from '@shared/provider/cognito-auth-provider.service';
import { SharedModule } from '@shared/shared.module';
import { ApplicationState } from '@shared/store/application/application.state';
import { AuthState } from '@shared/store/auth/auth.state';
import { LoaderState } from '@shared/store/loader/loader.state';
import { NavigationState } from '@shared/store/navigation/navigation.state';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslateConfig } from 'app/config/translate.config';
import { InboxState } from 'app/view/inbox/state/conversations/inbox.state';
import { LogBookState } from 'app/view/log-book/state/log-book/log-book.state';
import { defineLocale, nlLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerConfig, BsDatepickerModule, BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { NgPipesModule } from 'ngx-pipes';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule } from 'ngx-toastr';
import { MESSAGE_FORMAT_CONFIG } from 'ngx-translate-messageformat-compiler';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DashboardState } from 'app/view/dashboard/state/dashboard.state';
import { ApplicantsState } from 'app/view/patients/state/applicants/applicants.state';
import { TreatmentState } from 'app/view/patients/state/treatment/treatment.state';
import { PatientMoodboardState } from 'app/view/patients/state/patient-moodboard/patient-moodboard.state';
import { PatientPreviewState } from 'app/view/patients/state/patient-preview/patient-preview.state';
import { TrendMonitorMyPatientsState } from 'app/view/trend-monitor/state/my-patients/trend-monitor-my-patients.state';
import { NgToggleModule } from 'ng-toggle-button';
import { SelfHelpState } from 'app/view/self-help-library/store/self-help/self-help.state';
import { ConsultationsState } from 'app/view/patients/state/consultations/consultations.state';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import 'chartjs-adapter-luxon';
import { DailyNotesState } from 'app/view/patients/state/journal/daily-notes.state';
import { ClosureRequestsState } from 'app/view/patients/state/closure-requests/closure-request.state';
import { TeamState } from 'app/view/team/store/team-page/team.state';
import { PlannedTimeoutsState } from 'app/view/timeout/state/planned-timeouts/planned-timeouts.state';
import { PastTimeoutsState } from 'app/view/timeout/state/past-timeouts/past-timeouts.state';
import { NotificationsState } from 'app/view/notifications/store/notifications.state';
import { TeamMemberPreviewState } from 'app/view/team/store/team-preview/team-preview.state';
import { PatientsAtRiskState } from 'app/view/patients/state/patients-at-risk/patients-at-risk.state';
import { WaitingTimeoutsState } from 'app/view/timeout/state/waiting-timeouts/waiting-timeouts.state';
import { AcceptedTimeoutsState } from 'app/view/timeout/state/accepted-timeouts/accepted-timeouts.state';
import { DeclinedTimeoutsState } from 'app/view/timeout/state/declined-timeouts/declined-timeouts.state';
import { BuddyGroupsState } from 'app/view/buddy-groups/store/buddy-groups.state';
import { RejectedApplicantsState } from 'app/view/patients/state/rejected-applicants/rejected-applicants.state';

registerLocaleData(angularLocaleEn, LanguageVersion.EN);
registerLocaleData(angularLocaleNl, LanguageVersion.NL);

defineLocale('en-gb', enGbLocale);
defineLocale('nl', nlLocale);

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [ AppComponent ], imports: [ BrowserModule,
    AppRoutingModule,
    LayoutModule,
    NgPipesModule,
    TranslateModule.forRoot(TranslateConfig),
    AngularSvgIconModule.forRoot(),
    ModalModule.forRoot(),
    ProgressbarModule.forRoot(),
    ToastrModule.forRoot(ToastrConfig),
    NgSelectModule,
    BsDatepickerModule.forRoot(),
    ReactiveFormsModule,
    NavigationModule,
    NgToggleModule.forRoot(NgToggleConfig),
    NgxsModule.forRoot([
      ApplicationState,
      AuthState,
      NavigationState,
      LoaderState,
      InboxState,
      LogBookState,
      DashboardState,
      ApplicantsState,
      RejectedApplicantsState,
      TreatmentState,
      PatientMoodboardState,
      PatientPreviewState,
      SelfHelpState,
      ConsultationsState,
      PastTimeoutsState,
      PlannedTimeoutsState,
      TrendMonitorMyPatientsState,
      DailyNotesState,
      ClosureRequestsState,
      TeamState,
      TeamMemberPreviewState,
      NotificationsState,
      PatientsAtRiskState,
      WaitingTimeoutsState,
      AcceptedTimeoutsState,
      DeclinedTimeoutsState,
      BuddyGroupsState
    ], {
      developmentMode: !ApplicationConfig.production
    }),
    NgxsLoggerPluginModule.forRoot({
      disabled: ApplicationConfig.production
    }),
    NgxsStoragePluginModule.forRoot(NgxsStorageConfig),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: ApplicationConfig.production
    }),
    NgxsRouterPluginModule.forRoot(),
    NgxSpinnerModule.forRoot(NgxSpinnerConfig),
    FormsModule,
    SharedModule ], providers: [
    {
      provide: LOCALE_ID,
      useClass: LocaleId,
      deps: [ Store ]
    },
    { provide: AUTH_PROVIDER, useClass: CognitoAuthProvider },
    { provide: NgSelectConfig, useValue: { disableVirtualScroll: true, openOnEnter: false, outsideClickEvent: 'mousedown' } },
    { provide: BsDatepickerConfig, useFactory: getDatePickerConfig },
    { provide: BsDaterangepickerConfig, useFactory: getDateRangePickerConfig },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppLanguageInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: LoaderInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppVersionInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AppTimezoneInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: ErrorInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: AccessTokenInterceptor },
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: RefreshTokenInterceptor },
    { provide: MESSAGE_FORMAT_CONFIG, useValue: MessageFormatConfig },
    DatePipe,
    TranslatePipe,
    provideCharts(withDefaultRegisterables()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations()
  ]
})
export class AppModule {
  constructor() {
  }
}
